import {html, render as renderHTML} from 'lit-html'
import {install, uninstall} from '@github-ui/hotkey'
import type {CheckStepElement} from './check-step'
import {LogParser} from './log-parser'
import {areCharacterKeyShortcutsEnabled} from '@github-ui/hotkey/keyboard-shortcuts-helper'
// eslint-disable-next-line no-restricted-imports
import {observe} from '@github/selector-observer'
// eslint-disable-next-line no-restricted-imports
import {on} from 'delegated-events'
import {repeat} from 'lit-html/directives/repeat'
import {sendStats} from '@github-ui/stats'

const MAX_LOG_SIZE_IN_BYTES = 1024 * 1024 * 10 // 10Mb
const LINES_PER_CLUSTER = 50
const ESTIMATED_LINE_LENGTH = 20
let elementToScroll: HTMLElement | null = null
let shouldAutoScroll = true
let lastScrollTop: number | null = null
let scrolledUp = false

export const stepsMap: {[externalId: string]: Step} = {}

export function getUnorderedSteps(): Step[] {
  return Object.keys(stepsMap).map(x => stepsMap[x]!)
}

const scrollableContainer = () => document.querySelector('.js-full-logs-container') as HTMLElement
observe('.js-full-logs-container', {
  constructor: HTMLElement,
  add() {
    calculateContainerHeights()
    addScrollListener()
  },
})

const uxRefresh = () => {
  const logsContainer = scrollableContainer()
  const logsContainerHasUxRefreshAttribute = logsContainer?.hasAttribute('data-ux-refresh')
  return logsContainer && logsContainerHasUxRefreshAttribute
}

function scrollListener() {
  determineScrollDirection()
  renderSteps()
}

function addScrollListener() {
  const container = uxRefresh() ? document : scrollableContainer() || document
  /* eslint-disable-next-line github/prefer-observers */
  container.addEventListener('scroll', scrollListener)
}

function determineScrollDirection() {
  const newScrollTop = calculateScrollTop()
  if (lastScrollTop !== null) {
    if (!scrolledUp && newScrollTop < lastScrollTop) {
      scrolledUp = true
    }
  }
  lastScrollTop = newScrollTop
}

function calculateScrollTop(): number {
  return isFullscreen() ? fullLogsContainer().scrollTop : window.scrollY
}

function calculateContainerHeights() {
  if (uxRefresh() && !isFullscreen()) {
    scrollableContainer()?.style.removeProperty('height')
  } else {
    makeScrollable(scrollableContainer())
  }
}

function makeScrollable(element: HTMLElement | null) {
  if (!element) return
  element.style.height = `${window.innerHeight - element.getBoundingClientRect().top}px`
}

calculateContainerHeights()
addScrollListener()

/* eslint-disable-next-line github/prefer-observers */
window.addEventListener('resize', calculateContainerHeights)

export const enum StepState {
  INITIAL = 0,
  COMPLETED_LOGS_LOADING = 1,
  COMPLETED_LOGS_LOADED = 2,
  STREAMING = 3,
  ERROR = 4,
}

type Cluster = {
  startIndex: number
  endIndex: number
  wrapper: HTMLElement
  visible: boolean
  height?: number
}

type Step = {
  externalId: string
  number: number
  parser: LogParser
  container: HTMLElement
  searchLineIndexes: number[]
  clusters: Cluster[]
  state: StepState
  permalinkLine?: number
  expandedGroups: Set<number>
}

const {href, hash} = window.location
const baseURL = href.substring(0, href.length - hash.length)

const buffer: Record<string, string[]> = {}

export function flushLogs(step: Step) {
  const newLines = buffer[step.externalId]
  if (!newLines) return
  for (const stp of Object.values(stepsMap)) {
    if (step !== stp && !stp.container.hasAttribute('manually-opened')) closeStep(stp)
  }
  updateStreamingLogs(step, newLines, null)
  delete buffer[step.externalId]
}

export function bufferLogs(externalId: string, lines: string[]) {
  buffer[externalId] = (buffer[externalId] || []).concat(lines)
}

export function bufferedStepIds() {
  return Object.keys(buffer)
}

export function configureStep(container: HTMLElement, permalinkLine?: number): Step {
  const number = getStepNumber(container)
  const externalId = container.getAttribute('data-external-id') || ''
  const parser = new LogParser('', `${baseURL}#step:${number}`)

  const step: Step = {
    externalId,
    number,
    parser,
    container,
    searchLineIndexes: [],
    state: StepState.INITIAL,
    clusters: [],
    permalinkLine,
    expandedGroups: new Set<number>(),
  }

  moveStep(container)
  stepsMap[externalId] = step
  container.removeEventListener('toggle', handleToggle)
  container.addEventListener('toggle', handleToggle)
  container.removeEventListener('click', handleClick)
  container.addEventListener('click', handleClick)

  flushLogs(step)
  return step
}

function stepFromContainer(container: HTMLElement): Step | null {
  const externalId = container.getAttribute('data-external-id')
  if (!externalId) {
    return null
  }

  return stepsMap[externalId] || null
}

async function handleToggle(event: Event) {
  const container = event.currentTarget as HTMLElement
  const step = stepFromContainer(container)
  if (!step) {
    return
  }

  // This shouldn't be necessary, but we are seeing that heights sometimes are not calculated correctly when the page loads
  calculateContainerHeights()

  if (isOpen(step) && step.state !== StepState.COMPLETED_LOGS_LOADED) await loadCompletedLogs(step)
  renderSteps()
}

function handleClick(event: Event) {
  const container = event.currentTarget as HTMLElement
  const step = stepFromContainer(container)
  if (!step) {
    return
  }

  if (step.state !== StepState.STREAMING && !getCompletedLogURL(step)) {
    event.preventDefault()
    return
  }

  if (!step.container.hasAttribute('open')) {
    step.container.setAttribute('manually-opened', '')
  }
}

function isOpen(step: Step) {
  return step.container.hasAttribute('open')
}

function openStep(step: Step) {
  resetScrollToLogic()
  step.container.setAttribute('open', 'open')
}

function closeStep(step: Step) {
  step.container.removeAttribute('open')
  const element = step.container.closest<CheckStepElement>('check-step')
  if (element) {
    element.removeAttribute('data-in-progress')
    element.removeAttribute('manually-opened')
    element.update()
  }
}

function getCompletedLogURL(step: Step) {
  return step.container.getAttribute('data-log-url')
}

// Order steps in the UI according to their step number
function moveStep(stepContainer: Element) {
  // if descendant of check-step element (new non-postback UI)
  const isCheckStepChild = stepContainer.parentNode?.nodeName === 'CHECK-STEP'

  // if the stepContainer is a child of a check-step, we want to move the check-step parent
  const elementToMove = isCheckStepChild ? stepContainer.parentElement! : stepContainer
  const stepNumber = getStepNumber(elementToMove)

  const siblingElements = [...elementToMove.parentElement!.children]
  for (const siblingElement of siblingElements) {
    if (siblingElement === elementToMove) break
    const siblingStepNumber = getStepNumber(siblingElement)
    if (+stepNumber < +siblingStepNumber) {
      elementToMove.parentElement!.insertBefore(elementToMove, siblingElement)
    }
  }
}

function showLoader(step: Step) {
  const {loader, chevron} = queryElements(step)
  loader.removeAttribute('hidden')
  chevron.setAttribute('hidden', 'hidden')
}

function hideLoader(step: Step) {
  const {loader, chevron} = queryElements(step)
  loader.setAttribute('hidden', 'hidden')
  chevron.removeAttribute('hidden')
}

function enableStep(step: Step) {
  const {chevron} = queryElements(step)
  chevron.style.removeProperty('visibility')
  step.container.style.removeProperty('pointer-events')
}

export async function loadCompletedLogs(
  step: Step,
  options?: {keepLoader?: boolean; scrollToBottom?: boolean; permaLink?: boolean},
) {
  options = options || {}

  if (step.state === StepState.COMPLETED_LOGS_LOADING) return
  if (step.state === StepState.COMPLETED_LOGS_LOADED) return

  const url = getCompletedLogURL(step)
  if (!url) return

  resetScrollToLogic()
  if (options.scrollToBottom || options.permaLink) openStep(step)

  const {downloadErrorMessage, downloadErrorMessageText} = queryElements(step)
  downloadErrorMessage.hidden = true
  showLoader(step)
  resetStep(step)
  enableStep(step)

  try {
    step.state = StepState.COMPLETED_LOGS_LOADING

    const response = await fetch(url)

    // if the SSO token expires, we will get an HTML response instead of logs (with an non-error status code)
    // so let's check that the response type is text not html
    const isHTMLContent = response?.headers?.get('Content-Type')?.includes('text/html')

    if (!response.ok || isHTMLContent) {
      let text

      if (response.status === 410) {
        text = 'data-removed-text'
      } else if (response.status === 404) {
        text = 'data-not-found-text'
      } else {
        text = 'data-error-text'
      }

      const message = downloadErrorMessageText.getAttribute(text)!

      downloadErrorMessageText.textContent = message
      throw new Error('Response was not OK')
    }

    const reader = response.body && response.body.getReader && response.body.getReader()
    if (reader) {
      await streamCompletedLog(step, reader, options)
    } else {
      const data = await response.text()
      await appendLogs(step, data, options)
    }

    step.state = StepState.COMPLETED_LOGS_LOADED
    sendStats({incrementKey: 'ACTIONS_COMPLETED_LOG_VIEWS_SUCCEED'})

    if (options.scrollToBottom) {
      scrollToLine(step, step.parser.logLines.length - 1)
    }
  } catch (err) {
    step.state = StepState.ERROR
    downloadErrorMessage.hidden = false
    sendStats({incrementKey: 'ACTIONS_COMPLETED_LOG_VIEWS_FAIL'})
    throw err
  } finally {
    hideLoader(step)
  }
}

export async function updateStreamingLogs(step: Step, newLines: string[], startLine: number | null) {
  if (getCompletedLogURL(step)) return

  if (step.state !== StepState.STREAMING) {
    // change step to in progress while we wait for API update to finish
    // avoids awkward state of logs coming in before status icon has been updated
    const checkStepElement = step.container.closest<CheckStepElement>('check-step')
    if (checkStepElement) {
      checkStepElement.setAttribute('data-in-progress', '')
      // TODO: we can hack started_at here too
      checkStepElement.update()
    }
  }
  step.state = StepState.STREAMING

  if (newLines.length === 0) return

  const firstLines = step.parser.rawLogData.length === 0
  const container = fullLogsContainer()
  const lastElement = findLineElement(step, step.parser.logLines.length - 1)
  const lastElementBottom = lastElement
    ? lastElement.getBoundingClientRect().bottom
    : container.getBoundingClientRect().bottom
  const lastElementInView = isFullscreen()
    ? lastElementBottom < container.getBoundingClientRect().bottom
    : lastElementBottom < fullLogsContainerHeight()

  if (shouldAutoScroll) {
    // Break out of auto scroll when a scroll up is detected
    shouldAutoScroll = !scrolledUp
  } else {
    // Switch back to auto scroll when last element is in view
    shouldAutoScroll = lastElementInView
    if (shouldAutoScroll) {
      scrolledUp = false
    }
  }

  await appendLogs(step, newLines, {startLine})

  const {loader, logsContainer, downloadErrorMessage, animation} = queryElements(step)
  enableStep(step)

  // Remove the loader and display the logs
  if (step.parser.rawLogData.length > 0 && !loader.getAttribute('hidden')) {
    loader.setAttribute('hidden', 'hidden')
    logsContainer.hidden = false
  }
  // Hide any previous error message
  downloadErrorMessage.hidden = true
  // Improves the UX when we start receiving log lines but the partial
  // for the step summary hasn't loaded yet
  if (animation) animation.removeAttribute('hidden')

  if (firstLines) {
    openStep(step)
    sendStats({incrementKey: 'ACTIONS_STREAMING_LOG_VIEWS_SUCCEED'})
  }

  if (isOpen(step) && shouldAutoScroll) {
    lastScrollTop = calculateScrollTop()
    scrollToLine(step, step.parser.logLines.length - 1)
  }
}

async function streamCompletedLog(
  step: Step,
  reader: ReadableStreamReader<unknown>,
  options?: {keepLoader?: boolean; scrollToBottom?: boolean},
) {
  let bytes = 0
  const utf8Decoder = new TextDecoder('utf-8')
  for (;;) {
    // @ts-expect-error not sure what the expected type here should be
    const chunk = await reader.read()
    if (chunk.done) break
    // chunk.value is always an Uint8Array if done === true
    const value = chunk.value == null ? new Uint8Array(0) : (chunk.value as Uint8Array)

    bytes += value.length

    if (bytes > MAX_LOG_SIZE_IN_BYTES) {
      queryElements(step).truncatedMessage.hidden = false
      break
    }

    const data = utf8Decoder.decode(value)
    await appendLogs(step, data, options)
  }
}

function getStepNumber(stepDetails: Element): number {
  const attr = stepDetails.tagName === 'CHECK-STEP' ? 'data-number' : 'data-step-number'
  const num = stepDetails.getAttribute(attr)
  if (!num) return -1
  return +num
}

function resetStep(step: Step) {
  step.parser.setData('')
  step.clusters = []
  queryElements(step).logsContainer.textContent = ''
}

// We block virtualizing/materializing while scrolling. There are cases where we want to reset this logic.
function resetScrollToLogic() {
  elementToScroll = null
}

async function appendLogs(
  step: Step,
  data: string | string[],
  options?: {startLine?: number | null; keepLoader?: boolean; scrollToBottom?: boolean},
) {
  options = options || {}
  const {logsContainer} = queryElements(step)
  const lastCluster = step.clusters[step.clusters.length - 1]
  let startIndex = lastCluster ? lastCluster.startIndex : 0

  const lastLine = step.parser.logLines.length - 1
  if (typeof data === 'string') {
    step.parser.appendData(data)
  } else {
    step.parser.appendLines(data, options.startLine)
  }
  renderLines(step, [lastLine]) // re-render previous last line, just in case it's changed

  // Divide the log lines in clusters
  while (startIndex < step.parser.logLines.length) {
    const endIndex = Math.min(startIndex + LINES_PER_CLUSTER, step.parser.logLines.length) - 1

    if (lastCluster && lastCluster.startIndex === startIndex) {
      if (lastCluster.visible) {
        appendLines(step, lastCluster, lastCluster.endIndex + 1, endIndex)
      } else {
        estimateClusterHeight(step, lastCluster)
      }
      lastCluster.endIndex = endIndex
    } else {
      const wrapper = document.createElement('div')
      const cluster: Cluster = {
        startIndex,
        endIndex,
        wrapper,
        visible: false,
      }
      estimateClusterHeight(step, cluster)
      step.clusters.push(cluster)
    }

    startIndex += LINES_PER_CLUSTER
  }
  logsContainer.hidden = false
  if (!options.keepLoader) hideLoader(step)
  await syncDOM(step, true)
}

// For a very big log just the fact of adding many empty wrappers at the same time may freeze the browser.
// With this function we add wrappers one at a time.
async function syncDOM(step: Step, render: boolean) {
  const {logsContainer} = queryElements(step)
  const count = logsContainer.childElementCount
  if (step.clusters.length === count) return

  const cluster = step.clusters[count]!
  logsContainer.append(cluster.wrapper)

  // We just want to render the first clusters (the ones that are visible).
  // As soon as there's some that is not visible, we stop calculating their visibility
  // to speed up the process.
  if (render) renderCluster(step, cluster)

  return new Promise<void>(resolve => {
    setTimeout(async () => {
      await syncDOM(step, render && cluster.visible)
      resolve()
    }, 0)
  })
}

function estimateClusterHeight(step: Step, cluster: Cluster) {
  let i = cluster.startIndex
  let total = 0
  while (i <= cluster.endIndex) {
    const line = step.parser.logLines[i++]!
    const groupIndex = line.group?.lineIndex
    if (groupIndex == null || step.expandedGroups.has(groupIndex)) total++
  }

  if (!cluster.visible) {
    const estHeight = cluster.height ?? total * ESTIMATED_LINE_LENGTH
    cluster.wrapper.style.height = `${estHeight}px`
  }
}

function fullLogsContainer() {
  return scrollableContainer() || document.documentElement
}

function fullLogsContainerHeight() {
  // For UX refresh, use the entire window, unless in full screen mode
  return scrollableContainer() && (!uxRefresh() || isFullscreen())
    ? scrollableContainer().offsetHeight
    : window.innerHeight
}

function renderCluster(step: Step, cluster: Cluster) {
  const container = fullLogsContainer()
  const {wrapper, visible} = cluster

  const scrollTop = uxRefresh() && !isFullscreen() ? -container.getBoundingClientRect().top : container.scrollTop
  const above = scrollTop > wrapper.offsetTop + wrapper.offsetHeight
  const below = scrollTop + fullLogsContainerHeight() < wrapper.offsetTop

  if (above || below) {
    setClusterVisibility(step, cluster, false)
  } else if (!visible) {
    setClusterVisibility(step, cluster, true)
  }
}

function setClusterVisibility(step: Step, cluster: Cluster, visible: boolean) {
  const {wrapper} = cluster
  const selection = window.getSelection()

  if (selection && !selection.isCollapsed && selection.containsNode(wrapper, true)) visible = true
  if (visible === cluster.visible) return

  if (visible) {
    appendLines(step, cluster, cluster.startIndex, cluster.endIndex)
    wrapper.style.height = 'auto'
  } else {
    if (cluster.visible) cluster.height = wrapper.offsetHeight || cluster.height
    wrapper.style.height = `${cluster.height}px`
    wrapper.textContent = ''
  }
  cluster.visible = visible
}

function appendLines(step: Step, cluster: Cluster, startIndex: number, endIndex: number) {
  const lineIndexes = []
  for (let index = startIndex; index <= endIndex; index++) {
    lineIndexes.push(index)
  }
  const template = html`
    ${repeat(lineIndexes, lineIndex => {
      const tmpl = lineTemplate(step, lineIndex)
      if (!tmpl) return html`<div data-index="${lineIndex}" hidden></div>`
      return html`<div data-index="${lineIndex}">${tmpl}</div>`
    })}
  `
  const element = document.createElement('div')
  renderHTML(template, element)
  cluster.wrapper.appendChild(element)
}

function renderStep(step: Step) {
  if (step.state >= StepState.COMPLETED_LOGS_LOADED) {
    for (const cluster of step.clusters) {
      renderCluster(step, cluster)
    }
  }
}

function renderSteps() {
  // Don't attempt to re-render steps while we are actively scrolling towards an element. Once the element is within the view
  // we can start processing renders again
  if (elementToScroll) {
    const container = fullLogsContainer()
    const scrollTop = uxRefresh() ? -container.getBoundingClientRect().top : container.scrollTop
    const elemAbove = scrollTop > elementToScroll.offsetTop + elementToScroll.offsetHeight
    const elemBelow = scrollTop + fullLogsContainerHeight() < elementToScroll.offsetTop
    if (!elemAbove && !elemBelow) {
      elementToScroll = null
    }
  } else {
    for (const step of getUnorderedSteps()) {
      if (isOpen(step)) renderStep(step)
    }
  }
}

function queryElements(step: Step) {
  const logDisplayElement = step.container.querySelector<HTMLElement>('.js-checks-log-display')!
  return {
    logsContainer: logDisplayElement.querySelector<HTMLElement>('.js-checks-log-display-container')!,
    loader: step.container.querySelector<SVGElement>('.js-check-step-loader')!,
    downloadErrorMessage: logDisplayElement.querySelector<HTMLElement>('.js-checks-log-display-error')!,
    downloadErrorMessageText: logDisplayElement.querySelector<HTMLElement>('.js-checks-log-display-error-text')!,
    animation: step.container.querySelector('.js-check-step-icon-animation'),
    truncatedMessage: logDisplayElement.querySelector<HTMLElement>('.js-checks-log-display-truncated')!,
    chevron: step.container.querySelector<SVGElement>('.js-check-step-chevron')!,
  }
}

export function isFullscreen() {
  const checkSteps = document.querySelector<HTMLElement>('.js-check-steps')
  return !!checkSteps?.style?.position
}

export function searchStep(step: Step, text: string, selectFirstLine: boolean) {
  cancelSearchStep(step)

  const searchLineIndexes = step.parser.search(text, selectFirstLine)
  step.searchLineIndexes = searchLineIndexes
  if (selectFirstLine && searchLineIndexes.length > 0) {
    renderLines(step, step.searchLineIndexes)
    scrollToLine(step, searchLineIndexes[0]!, {select: true})
  }
  return searchLineIndexes
}

export function cancelSearchStep(step: Step) {
  step.parser.cancelSearch()
  renderLines(step, step.searchLineIndexes)
  step.searchLineIndexes = []
}

function renderLine(step: Step, index: number, element: HTMLElement) {
  const template = lineTemplate(step, index)
  if (template === null) {
    element.hidden = true
    return
  }
  element.hidden = false
  renderHTML(template, element)
}

function lineTemplate(step: Step, index: number) {
  const line = step.parser.logLines[index]!
  const groupIndex = line.group?.lineIndex
  if (groupIndex != null && !step.expandedGroups.has(groupIndex)) return null

  const groupExpanded = line.isGroup && step.expandedGroups.has(index)
  return step.parser.renderTemplate(line, !!groupExpanded, false)
}

function renderLines(step: Step, lineIndexes: number[]) {
  for (const index of lineIndexes) {
    const element = findLineElement(step, index)
    if (element) renderLine(step, index, element)
  }
}

function findLineElement(step: Step, lineIndex: number) {
  return step.container.querySelector(`[data-index='${lineIndex}']`) as HTMLElement
}

export function scrollToLine(step: Step, lineIndex: number, options?: {select?: boolean}) {
  const {select} = options || {}
  openStep(step)

  // Before scrolling ensure that we have materialized at least +/- 50 lines on either side
  // This ensures we do not materialize another cluster once we scroll to the index
  // which would change the visible elements
  const skipVirtualization = step.clusters.length < 100
  for (const cluster of step.clusters) {
    if (
      skipVirtualization ||
      (lineIndex - LINES_PER_CLUSTER <= cluster.endIndex && lineIndex + LINES_PER_CLUSTER >= cluster.startIndex)
    ) {
      setClusterVisibility(step, cluster, true)
    }
  }

  // Additionally render neighboring clusters in case we step into a small cluster in betwen two other open clusters.
  const orderedSteps = getUnorderedSteps().sort((a, b) => a.number - b.number)
  const stepIndex = orderedSteps.findIndex(x => x.number === step.number)
  ensureEnoughMaterializedLines(orderedSteps, stepIndex, LINES_PER_CLUSTER - (lineIndex + 1), true)
  const linesAwayFromEnd = LINES_PER_CLUSTER - (step.clusters[step.clusters.length - 1]!.endIndex - lineIndex)
  ensureEnoughMaterializedLines(orderedSteps, stepIndex, linesAwayFromEnd, false)

  const element = findLineElement(step, lineIndex)
  if (!element) return

  const {hidden} = element
  elementToScroll = element
  // The only reason for a line to be hidden is that it's in a collapsed group
  if (hidden) expandGroup(step, lineIndex)
  if (hidden && select) renderLine(step, lineIndex, element)
  if (select) selectLine(element)

  // Ideally we don't do this but we only add expanded groups after the grouptoggle handler is triggered.
  setTimeout(async () => {
    elementToScroll?.scrollIntoView({behavior: skipVirtualization ? 'smooth' : 'auto', block: 'center'})
  }, 0)
}

function ensureEnoughMaterializedLines(
  orderedSteps: Step[],
  currentStep: number,
  linesToMaterialize: number,
  above: boolean,
) {
  let linesLeft = linesToMaterialize

  const increment = above ? -1 : 1
  let i = currentStep + increment
  while (linesLeft > 0 && ((above && i >= 0) || (!above && i < orderedSteps.length))) {
    if (orderedSteps[i]!.clusters?.length > 0 && isOpen(orderedSteps[i]!)) {
      const neighborStep: Step = orderedSteps[i]!
      const clusterIndex = above ? neighborStep.clusters.length - 1 : 0
      const nearestNeighborCluster = neighborStep.clusters[clusterIndex]!
      setClusterVisibility(neighborStep, nearestNeighborCluster, true)
      linesLeft -= nearestNeighborCluster.endIndex - nearestNeighborCluster.startIndex + 1
    }
    i += increment
  }
}

function expandGroup(step: Step, lineIndex: number) {
  const line = step.parser.logLines[lineIndex]!
  const groupIndex = line.group?.lineIndex
  if (groupIndex == null) return
  step.expandedGroups.add(groupIndex)
  const details = step.container.querySelector(`[data-index='${groupIndex}'] details`)
  if (details) details.setAttribute('open', 'open')
}

function selectLine(element: HTMLElement) {
  for (const prev of document.querySelectorAll('.js-check-step-line.selected')) {
    prev.classList.remove('selected')
  }
  element.querySelector<HTMLElement>('.js-check-step-line')!.classList.add('selected')
}

export function findStep(childElement: Element) {
  const container = childElement.closest<HTMLElement>('.js-checks-log-details')!
  return getUnorderedSteps().find(step => step.container === container)
}

export function toggleGroup(step: Step, index: number, open: boolean) {
  open ? step.expandedGroups.add(index) : step.expandedGroups.delete(index)

  const lines = step.parser.logLines.filter(line => line.group?.lineIndex === index)
  if (lines.length === 0) return

  const lineIndexes = lines.map(line => line.lineIndex)
  const minLine = lineIndexes[0]!
  const maxLine = lineIndexes[lineIndexes.length - 1]!

  for (const cluster of step.clusters) {
    if (cluster.startIndex <= maxLine && cluster.endIndex >= minLine) {
      estimateClusterHeight(step, cluster)
      // This should already be visible if we are scrolling
      if (!elementToScroll) {
        renderCluster(step, cluster)
      }
      if (cluster.visible) {
        const clusterLineIndexes = lineIndexes.filter(idx => idx >= cluster.startIndex && idx <= cluster.endIndex)
        renderLines(step, clusterLineIndexes)
      }
    }
  }

  // clusters after the group may have change visibility
  renderSteps()
}

export function selectPermalinkLine(step: Step, permalinkLine: number, scroll: boolean) {
  const lineIndex = step.parser.indexFromNumber(permalinkLine)
  if (scroll) {
    scrollToLine(step, lineIndex, {select: true})
  } else {
    const element = findLineElement(step, lineIndex)
    if (element) selectLine(element)
  }
}

function toggleFullScreen() {
  const checkSteps = document.querySelector<HTMLElement>('.js-check-steps')!

  setFullScreenKeyboardShortcuts()
  lastScrollTop = null

  if (isFullscreen()) {
    // Turn the logs container back into what it was before becoming full screen
    if (uxRefresh()) {
      const container = fullLogsContainer()
      const stepHeaders = container.querySelectorAll<HTMLElement>('.js-check-step-summary')!
      const logToolbar = document.querySelector<HTMLElement>('.js-checks-log-toolbar')!

      scrollableContainer()?.removeEventListener('scroll', scrollListener)
      /* eslint-disable-next-line github/prefer-observers */
      document.addEventListener('scroll', scrollListener)

      container.classList.remove('overflow-y-scroll')
      logToolbar.classList.add('uxr_CheckRun-header')

      for (const stepHeader of stepHeaders) {
        stepHeader.classList.add('uxr_CheckStep-header')
        stepHeader.classList.remove('top-0')
        stepHeader.style.removeProperty('sticky')
      }
    }

    for (const prop of ['position', 'top', 'left', 'right', 'bottom', 'zIndex']) {
      checkSteps.style.removeProperty(prop)
    }
    checkSteps.classList.add('rounded-md-1')
  } else {
    // Turn the logs container into a full screen scrollable element
    if (uxRefresh()) {
      const container = fullLogsContainer()
      const stepHeaders = container.querySelectorAll<HTMLElement>('.js-check-step-summary')!
      const logToolbar = document.querySelector<HTMLElement>('.js-checks-log-toolbar')!

      /* eslint-disable-next-line github/prefer-observers */
      scrollableContainer()?.addEventListener('scroll', scrollListener)
      document.removeEventListener('scroll', scrollListener)

      container.classList.add('overflow-y-scroll')
      logToolbar.classList.remove('uxr_CheckRun-header')

      for (const stepHeader of stepHeaders) {
        stepHeader.classList.remove('uxr_CheckStep-header')
        stepHeader.classList.add('top-0')
        stepHeader.style.position = 'sticky'
      }
    }

    checkSteps.style.position = 'fixed'
    checkSteps.style.left = '0'
    checkSteps.style.right = '0'
    checkSteps.style.top = '0'
    checkSteps.style.bottom = '0'
    checkSteps.style.zIndex = '1000'
    checkSteps.classList.remove('rounded-md-1')
  }
  calculateContainerHeights()
  renderSteps()
}

// Set appropriate keyboard shortcuts, shift + f should always toggle while Esc should be available only to exit while in full-screen mode
// https://github.com/github/hotkey
function setFullScreenKeyboardShortcuts() {
  const fullScreenToggle = document.querySelector<HTMLElement>('.js-checks-log-fullscreen')!
  uninstall(fullScreenToggle)

  if (areCharacterKeyShortcutsEnabled()) {
    fullScreenToggle.setAttribute('data-hotkey', isFullscreen() ? 'Shift+F' : 'Shift+F,Escape')
  } else {
    fullScreenToggle.setAttribute('data-hotkey', isFullscreen() ? '' : 'Escape')
  }
  /* eslint-disable-next-line github/no-dataset */
  install(fullScreenToggle, fullScreenToggle.dataset.shortcut)
}

on('change', '.js-checks-log-fullscreen', toggleFullScreen)
